import type React from "react";

import {faDownload, faFileLines} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import CustomButton from "~/component/common/CustomButton";

import {useLogging} from "./LoggingContext";

const LogViewer: React.FC = () => {
  const {logs, clearLogs, isVisible, toggleVisibility, exportLogs, enabled} =
    useLogging();

  // Don't render anything if logging is not enabled
  if (!enabled) {
    return null;
  }

  if (!isVisible) {
    return (
      <CustomButton
        onClick={toggleVisibility}
        className="fixed bottom-2 right-2 text-white p-2 rounded-full z-50"
        style={{width: "40px", height: "40px"}}
        aria-label="Show logs"
      >
        <FontAwesomeIcon
          icon={faFileLines}
          size="xl"
          className="text-white p-2 -m-2 rounded-full pl-2.5 pr-2.5 bg-gradient-to-br from-green-600 via-green-500 to-black"
        />
      </CustomButton>
    );
  }

  return (
    <div
      className="fixed bottom-0 left-0 right-0 bg-black/90 text-white z-50 max-h-[50vh] overflow-auto"
      style={{maxHeight: "50vh"}}
    >
      <div className="flex justify-between items-center p-2 border-b border-gray-700 sticky top-0 bg-black">
        <h3 className="text-lg font-semibold">Console Logs</h3>
        <div className="flex space-x-2">
          <button
            onClick={exportLogs}
            className="bg-green-600 text-white px-2 py-1 rounded text-sm flex items-center"
            title="Download logs as text file"
            aria-label="Export logs as text file"
          >
            <FontAwesomeIcon icon={faDownload} className="mr-1" />
            Export
          </button>
          <button
            onClick={clearLogs}
            className="bg-red-600 text-white px-2 py-1 rounded text-sm"
            aria-label="Clear all logs"
          >
            Clear
          </button>
          <button
            onClick={toggleVisibility}
            className="bg-gray-600 text-white px-2 py-1 rounded text-sm"
            aria-label="Hide log viewer"
          >
            Hide
          </button>
        </div>
      </div>

      <div className="p-2">
        {logs.length === 0 ? (
          <p className="text-gray-400 italic">No logs to display</p>
        ) : (
          logs.map((log, index) => (
            <div
              key={index}
              className={`mb-1 p-1 border-l-4 ${getLogTypeStyles(log.type)}`}
            >
              <div className="flex items-start">
                <span className="text-xs text-gray-400 mr-2">
                  {formatTimestamp(log.timestamp)}
                </span>
                <span
                  className={`text-xs font-bold mr-2 ${getLogTypeTextColor(log.type)}`}
                >
                  {log.type.toUpperCase()}
                </span>
                <div className="text-sm flex-1 overflow-x-auto">
                  <pre
                    className="whitespace-pre-wrap break-words overflow-hidden text-wrap max-w-full"
                    style={{wordBreak: "break-all"}}
                  >
                    {log.message}
                  </pre>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Helper functions
const getLogTypeStyles = (type: string): string => {
  switch (type) {
    case "error":
      return "border-red-500 bg-red-900/30";
    case "warn":
      return "border-yellow-500 bg-yellow-900/30";
    case "info":
      return "border-blue-500 bg-blue-900/30";
    default:
      return "border-gray-500 bg-gray-900/30";
  }
};

const getLogTypeTextColor = (type: string): string => {
  switch (type) {
    case "error":
      return "text-red-500";
    case "warn":
      return "text-yellow-500";
    case "info":
      return "text-blue-500";
    default:
      return "text-gray-300";
  }
};

const formatTimestamp = (date: Date): string => {
  return date.toLocaleTimeString([], {hour12: false});
};

export default LogViewer;
